import type { VideoJsPlayer } from 'video.js';
import type videojs from 'video.js';

import type { PlayerSources } from '../player.config';
import type { Plugins, TestingOptions } from '../player.interface';

import { useCallback, useMemo, useRef, useState } from 'react';

import { usePlayHandler } from './use-play-handler';
import { useLogger } from '../../../contexts/logger-context';
import { PlayerError } from '../player-errors';
import { useHiveTech } from '../tech/use-hive-tech';
import { useHtmlTech } from '../tech/use-html-tech';
import { useKollectiveTech } from '../tech/use-kollective-tech';
import { useVBrickTech } from '../tech/use-vbrick-tech';

const Tech = {
  kollective: 'kollective',
  vbrick: 'vbrick',
  hive: 'hive',
  html5: 'html5',
} as const;

export function useSetSource(
  player: VideoJsPlayer,
  sources: PlayerSources,
  plugins: Plugins,
  autoplay: boolean,
  preferredQuality?: string,
  testing?: TestingOptions
): {
  sourceIsSet: boolean;
  reloadSource: () => void;
  setVideoSrc: (videoSources: videojs.Tech.SourceObject[], force?: boolean) => Promise<void>;
} {
  const { logger: mainLogger } = useLogger();
  const logger = useMemo(() => mainLogger.getSubLogger({ name: 'SetSource' }), [mainLogger]);

  const [sourceIsSet, setSourceIsSet] = useState(false);
  const { autoplay: autoplayFunc, play: playFnc } = usePlayHandler(player);
  const useTestingRef = useRef(testing);
  useTestingRef.current = testing;

  const setVideoSrc = useCallback(
    async (videoSources: videojs.Tech.SourceObject[], force?: boolean) => {
      logger.debug('Try to set the video sources:', videoSources);

      // Empty sources are not allowed; skip setting the source
      if (!videoSources) {
        logger.warn('No video sources provided, skipping source setting.');
        return;
      }

      const skipping = useTestingRef.current?.skipSetSameSourceTwiceCheck ?? false;
      if (skipping) {
        logger.warn('Skipping same source check due to testing configuration.');
      }

      if (!force && !skipping) {
        const currentSrc = player.currentSrc();
        const hasVideoSrc = videoSources.find((src) => src.src === currentSrc);

        // Do not allow setting the same source again to avoid unnecessary reloading
        if (hasVideoSrc) {
          logger.warn('Source is already set, skipping source setting.');
          return;
        }
      }

      try {
        setSourceIsSet(false);
        logger.info('Setting video sources:', videoSources);

        const wasPlaying = !player.paused();
        if (wasPlaying) {
          player.pause();
        }

        player.src(videoSources);

        if (wasPlaying) {
          await playFnc();
        } else if (autoplay) {
          await autoplayFunc();
        }

        logger.info('Source set successfully.', videoSources);
        setSourceIsSet(true);
      } catch (error) {
        logger.error('Error setting video sources:', error);
        if (error instanceof PlayerError) {
          player.error(error.message);
        }
      }
    },
    [logger, autoplay, autoplayFunc, playFnc, player]
  );

  const { kollective: kollectiveConfig, ramp: vbrickConfig, hive: hiveConfig } = plugins || {};

  const tech = useMemo(() => {
    if (!sources.hls) {
      return Tech.html5;
    }

    if (kollectiveConfig) {
      return Tech.kollective;
    }
    if (vbrickConfig) {
      return Tech.vbrick;
    }
    if (hiveConfig) {
      return Tech.hive;
    }

    return Tech.html5;
  }, [sources.hls, kollectiveConfig, vbrickConfig, hiveConfig]);

  const { reloadSource: reloadKollectiveSource } = useKollectiveTech(
    tech === Tech.kollective,
    player,
    setVideoSrc,
    kollectiveConfig,
    sources.hls
  );
  const { reloadSource: reloadHiveSource } = useHiveTech(tech === Tech.hive, player, setVideoSrc, hiveConfig);
  const { reloadSource: reloadVBrickSource } = useVBrickTech(
    tech === Tech.vbrick,
    player,
    setVideoSrc,
    vbrickConfig,
    sources.hls
  );
  const { reloadSource: reloadHtmlSource } = useHtmlTech(tech === Tech.html5, setVideoSrc, sources, preferredQuality);

  const reloadSource = useCallback(() => {
    switch (tech) {
      case Tech.kollective:
        reloadKollectiveSource();
        break;
      case Tech.vbrick:
        reloadVBrickSource();
        break;
      case Tech.hive:
        reloadHiveSource();
        break;
      default:
        reloadHtmlSource();
    }
  }, [tech, reloadHiveSource, reloadVBrickSource, reloadHtmlSource, reloadKollectiveSource]);

  return { sourceIsSet, reloadSource, setVideoSrc };
}
